import React from "react";
import { useAlertLogsInfinite } from "@/alerts/api";
import { Button } from "@/components/ui/button";

// Alert status mapping and timeline helpers
const getAlertStatusFromType = (alertType: string): 'ok' | 'pending' | 'firing' => {
  if (alertType.includes('_to_ok') || alertType === 'ok') {
    return 'ok';
  } else if (alertType.includes('_to_pending') || alertType === 'pending') {
    return 'pending';
  } else if (alertType.includes('_to_firing') || alertType === 'firing') {
    return 'firing';
  }
  // Default status based on common patterns
  return 'ok';
};

const getStatusColor = (status: 'ok' | 'pending' | 'firing') => {
  switch (status) {
    case 'ok':
      return 'bg-green-500';
    case 'pending':
      return 'bg-amber-500';
    case 'firing':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

interface AlertTimelineProps {
  alertId: number;
}

export const AlertTimeline = ({ alertId }: AlertTimelineProps) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  } = useAlertLogsInfinite({ alert_id: alertId });

  // Flatten all alert logs from all pages and sort by time (most recent first)
  const allLogs = data?.pages.flatMap(page => page.results) || [];
  const sortedLogs = [...allLogs].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

  if (status === 'pending') {
    return <div className="text-sm text-gray-500">Loading timeline...</div>;
  }

  if (status === 'error') {
    return <div className="text-sm text-red-500">Error loading timeline</div>;
  }

  if (sortedLogs.length === 0) {
    return <div className="text-sm text-gray-500">No timeline data available</div>;
  }

  // Create timeline segments - need to think chronologically (oldest to newest)
  const chronologicalLogs = [...sortedLogs].reverse(); // Reverse to get oldest first
  const segments = [];

  for (let i = 0; i < chronologicalLogs.length; i++) {
    const log = chronologicalLogs[i];
    const nextLog = chronologicalLogs[i + 1];

    const startTime = new Date(log.time);
    const endTime = nextLog ? new Date(nextLog.time) : new Date(); // Current time if this is the latest log

    const duration = endTime.getTime() - startTime.getTime(); // Duration this status was active (positive)
    const status = getAlertStatusFromType(log.alert_type);

    // Only add segments with positive duration
    if (duration > 0) {
      segments.push({
        status,
        startTime,
        endTime,
        duration,
        log
      });
    }
  }

  // Calculate relative widths for timeline segments
  const totalDuration = segments.reduce((sum, seg) => sum + seg.duration, 0);
  const segmentsWithWidths = segments.map(seg => ({
    ...seg,
    widthPercent: totalDuration > 0 ? (seg.duration / totalDuration) * 100 : 100 / segments.length
  }));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Status Timeline</span>
        <span className="text-xs text-gray-500">{sortedLogs.length} status changes</span>
      </div>

      {/* Timeline bar */}
      <div className="flex h-4 rounded-full overflow-hidden border border-gray-200">
        {segmentsWithWidths.map((segment, index) => (
          <div
            key={index}
            className={`${getStatusColor(segment.status)} transition-all duration-200 hover:opacity-80`}
            style={{ width: `${Math.max(segment.widthPercent, 2)}%` }} // Minimum 2% width for visibility
            title={`${segment.status.toUpperCase()}: ${segment.startTime.toLocaleString()} - ${segment.endTime.toLocaleString()}`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center space-x-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
          <span>OK</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-amber-500 rounded-sm"></div>
          <span>Pending</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
          <span>Firing</span>
        </div>
      </div>

      {/* Load Previous button */}
      {hasNextPage && (
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading...' : 'Load Previous'}
          </Button>
        </div>
      )}
    </div>
  );
};
